import { useEffect, useState } from "react";
import CompetitionReplayModal from "./CompetitionReplayModal.jsx";
import { publicReplayProps } from "./replayShared.jsx";
import Footer from "./Footer.jsx";

// THE PUBLIC WINNERS LIST.
//
// Every competition ends with the image revealed — by somebody guessing
// it or by the lottery — so a finished competition has nothing left to
// hide. This is where they go afterwards, and it is the first page on
// this site that outlives the competition it describes.
//
// READS THE SITE'S OWN ENDPOINT, /api/winners, which follows the
// "Enable Winners Page" site setting. The partner API has its own
// /api/v1/winners with its own per-column toggles — one governs what
// this site displays, the other what a third party may consume.
//
// USERNAMES ARE MASKED unless the operator turns winnersUnmasked on. The
// server decides that; this page renders whatever it is given and must
// not try to be cleverer about it.
//
// Added 18 Sep 2026.
export default function WinnersPage({ demoRouteToken = null }) {
  const [winners, setWinners] = useState(null);
  const [failed, setFailed] = useState(false);

  // WATCHED IN PLACE, rather than on a page of its own.
  //
  // /replay/<id> still exists and still works — it is what somebody
  // shares, and what a middle-click or "copy link" gives them. The
  // click is intercepted so an ordinary one keeps you on the list you
  // were reading, which is where you want to be when the replay ends.
  //
  // Requested 18 Sep 2026.
  const [replayId, setReplayId] = useState(null);


  useEffect(() => {
    let cancelled = false;
    // THE SITE'S OWN ENDPOINT, not the partner API. That one follows
    // the API field toggles and returns only the columns a PARTNER may
    // consume; this page is not a partner and shows every column.
    // THE ROUTE TOKEN DECIDES WHICH WINNERS THESE ARE. On a test link
    // the server answers with test winners; without it, live ones.
    const qs = demoRouteToken
      ? `?limit=50&demoRouteToken=${encodeURIComponent(demoRouteToken)}`
      : "?limit=50";
    fetch(`/api/winners${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (!cancelled) setWinners(Array.isArray(data?.winners) ? data.winners : []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [demoRouteToken]);

  const money = (amount, currency) => {
    if (amount == null) return null;
    const symbol = { GBP: "£", USD: "$", EUR: "€" }[currency] || "";
    return `${symbol}${Number(amount).toFixed(2)}`;
  };

  return (
    // NO <SiteHeader /> HERE. main.jsx renders it for every non-admin
    // page, so a page that renders its own gets two — which is exactly
    // what happened on 18 Sep 2026. ContentPage.jsx says the same in its
    // own comment: "SiteHeader above, this centered column below".
    <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 20px 40px" }}>
        <h2>Winners</h2>

        {failed && (
          <p style={{ opacity: 0.8 }}>
            The winners list could not be loaded. Try again shortly.
          </p>
        )}

        {!failed && winners === null && <p style={{ opacity: 0.7 }}>Loading…</p>}

        {!failed && winners !== null && winners.length === 0 && (
          <p style={{ opacity: 0.8 }}>
            No competitions have been won yet. The first one will appear here.
          </p>
        )}

        {winners?.map((w) => (
          <div
            key={w.competitionId}
            style={{
              borderBottom: "1px solid var(--border-color-subtle)",
              padding: "14px 0",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <strong>{w.title}</strong>
              {w.prize != null && (
                <strong style={{ color: "var(--accent-green)" }}>
                  {money(w.prize, w.currency)}
                </strong>
              )}
            </div>

            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
              {/* A LOTTERY WIN HAS NO GUESSER, so there is no name to
                  print. Saying "won by the lottery" is the truth rather
                  than an invented winner. */}
              {w.winner ? `Won by ${w.winner}` : "Won by the lottery draw"}
              {w.winningEntry && (
                <>
                  {" — the answer was "}
                  <strong>{w.winningEntry}</strong>
                </>
              )}
            </div>

            <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 4 }}>
              {w.wonAt ? new Date(w.wonAt).toLocaleString() : null}
              {/* THE LINK THE PAGE PROMISED. Said "Replay recorded"
                  from v1.357.1 until the public viewer existed, because
                  a link that 404s is worse than saying nothing.

                  The route token travels with it, so a test-link
                  visitor's replay link stays on the test route. */}
              {w.hasReplay && (
                <>
                  {" · "}
                  {/* A PLAIN LINK. The preview token is kept in the
                      session, not in every URL — currentPreview falls
                      back to readStored() — so navigating to /replay/
                      stays in whichever mode this visitor is in.
                      Appending it here would put a route token in a URL
                      somebody might share. */}
                  <a
                    href={`/replay/${w.competitionId}`}
                    onClick={(e) => {
                      // A MODIFIED CLICK IS A REQUEST FOR A NEW TAB.
                      // Swallowing it would break the one thing a real
                      // link gives you over a button.
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      setReplayId(w.competitionId);
                    }}
                  >
                    Watch the replay
                  </a>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* THE SAME COMPONENT THE PAGE AT /replay/<id> USES, given the
          same public routes. One playback, three callers — an admin,
          a buyer, and this. */}
      {replayId && (
        <CompetitionReplayModal
          {...publicReplayProps(replayId, demoRouteToken)}
          onClose={() => setReplayId(null)}
        />
      )}
      <Footer />
    </div>
  );
}
